import { useEffect, useState } from 'react';
import type { ActiviteDuCours } from './canal';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-18 — LA FENÊTRE D'INACTIVITÉ, CÔTÉ ÉCRAN.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Si personne n'écrit, ne circule entre les tableaux ni ne manipule son espace de
 * fichiers pendant **quinze minutes**, elle s'affiche **à tout le monde en même temps** »
 * (Q290, Q407, Q440) ; « celui qui ne clique pas dans les **cinq minutes** est
 * déconnecté » (Q220, Q227).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE FICHIER NE DÉCIDE PAS QUAND LA FENÊTRE S'OUVRE. IL LA MONTRE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **L'autorité est le serveur, et l'annonce arrive par le canal** — c'est ce qui fait que
 * tout le monde la voit au même instant. Ce que ce code calcule est **le compte à rebours
 * seul**, depuis l'instant que le serveur a annoncé et les deux délais qu'il a envoyés
 * avec. Aucun délai n'est écrit ici : les recopier en ferait une seconde vérité que
 * personne ne rapprocherait de `regles.ts` le jour où elles changent.
 *
 * **`fenetreOuverte` VIENT DU SERVEUR ET CE CODE LE RECALCULE QUAND MÊME, ET CE N'EST PAS
 * UNE DÉFIANCE.** L'annonce ne repart qu'au **changement d'instant** : entre deux
 * annonces, l'écran doit voir la fenêtre s'ouvrir tout seul, sinon elle apparaîtrait avec
 * un retard qui pourrait aller jusqu'à quinze minutes. Le drapeau du serveur est donc le
 * point de départ, et l'horloge locale fait le reste — **sur la même valeur et la même
 * règle**, donc sans jamais diverger de plus d'un tour d'horloge.
 *
 * **IL N'Y A PAS D'ÉTAT DE SURSIS ICI, ET LE CORPUS L'INTERDIT** : « le sursis n'est pas
 * un état » (Q284). Une personne qui a répondu reste présente, et rien n'est mémorisé de
 * sa réponse — c'est l'annonce suivante, portant un instant plus frais, qui referme la
 * fenêtre pour tout le monde.
 */

/** Ce que l'écran a besoin de savoir de la fenêtre, à cet instant. */
export type EtatDeLaFenetre = {
  /** Vrai quand la fenêtre est posée et qu'il faut la montrer. */
  readonly ouverte: boolean;
  /** Secondes restantes avant l'échéance, jamais négatives. */
  readonly resteSecondes: number;
};

const FERMEE: EtatDeLaFenetre = { ouverte: false, resteSecondes: 0 };

/**
 * **La règle d'affichage, pure et donc lisible sans horloge.**
 *
 * `maintenant` est un argument et non `Date.now()` : une fonction qui lit l'heure
 * elle-même ne se relit pas, et ne se mesure pas.
 */
export function fenetreALinstant(
  activite: ActiviteDuCours | null,
  maintenant: number,
): EtatDeLaFenetre {
  if (activite === null) return FERMEE;

  const depart = Date.parse(activite.derniereActiviteA);
  if (Number.isNaN(depart)) return FERMEE;

  const poseeA = depart + activite.fenetreSecondes * 1_000;
  const echeanceA = poseeA + activite.reponseSecondes * 1_000;
  if (maintenant < poseeA) return FERMEE;

  return {
    ouverte: true,
    resteSecondes: Math.max(0, Math.ceil((echeanceA - maintenant) / 1_000)),
  };
}

/**
 * **L'horloge, et elle ne bat QUE pendant les cinq minutes qui se comptent.**
 *
 * **Un battement permanent serait un rendu par seconde pendant tout le cours**, sur la
 * surface dont « la latence du trait est le critère numéro un » (`DESIGN.md`) — et il
 * tournerait quatorze minutes sur quinze pour ne rien afficher. Tant que la fenêtre n'est
 * pas posée, il n'y a donc **qu'une seule minuterie**, réglée sur l'instant exact où elle
 * se posera ; la seconde par seconde ne commence qu'à ce moment-là.
 *
 * **Et elle se rerègle à chaque annonce** : une activité qui repousse la fenêtre change
 * l'instant, donc l'effet se rejoue et la minuterie repart de plus loin.
 */
export function useFenetreDInactivite(
  activite: ActiviteDuCours | null,
): EtatDeLaFenetre {
  const [maintenant, poserLInstant] = useState(() => Date.now());
  const etat = fenetreALinstant(activite, maintenant);

  useEffect(() => {
    if (activite === null) return;
    const depart = Date.parse(activite.derniereActiviteA);
    if (Number.isNaN(depart)) return;

    // **Aucun `setState` synchrone ici, et ce n'est pas une contrainte subie.** L'état
    // rendu se recalcule déjà au rendu, sur l'annonce fraîche ; le seul instant qui
    // doive être relu est celui d'une échéance, et il l'est **dans les rappels** de la
    // minuterie et du battement. Poser l'heure dans le corps de l'effet ferait un rendu
    // en cascade à chaque annonce, sur la surface dont la latence est le critère un.
    const poseeA = depart + activite.fenetreSecondes * 1_000;
    const attente = poseeA - Date.now();
    if (attente > 0) {
      // **Une seule minuterie tant qu'il n'y a rien à compter.** À son échéance, l'état
      // recalculé passe `ouverte` à vrai, ce qui **rejoue cet effet** — et c'est là que
      // la seconde par seconde commence. Sans `ouverte` dans les dépendances, la
      // minuterie tomberait et plus rien ne battrait.
      const minuterie = setTimeout(() => poserLInstant(Date.now()), attente + 50);
      return () => clearTimeout(minuterie);
    }

    const battement = setInterval(() => poserLInstant(Date.now()), 1_000);
    return () => clearInterval(battement);
  }, [activite, etat.ouverte]);

  return etat;
}
